import { useEffect, useState } from 'react';
import JWModal from '../brand/JWModal';
import JWButton from '../brand/JWButton';
import axiosService from '../../utils/axios';
import TastePicker from '../../pages/Onboarding/TastePicker';
import { Artist, toFavArtist } from '../../pages/Onboarding/ArtistPicker';
import { PRIMARY_BUTTON_CLASS } from '../../pages/Onboarding/OnboardingUI';
import { SpotifyArtistType } from '../../types/spotify';

interface AddInfluencesModalProps {
    show: boolean
    setShow: (show: boolean) => void
    onClose: () => void
    profileId?: string
    selectedArtists: SpotifyArtistType[]
    selectedGenres: string[]
    onSaved: (update: { favArtists: SpotifyArtistType[]; favGenres: string[] }) => void
}

const toArtist = (artist: SpotifyArtistType): Artist => ({
    id: artist.id,
    name: artist.name,
    displayText: artist.displayText || artist.name,
    url: artist.url || artist.external_urls?.spotify || null,
});

function AddInfluencesModal(props: AddInfluencesModalProps) {
    const [genres, setGenres] = useState<string[]>([]);
    const [artists, setArtists] = useState<Artist[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!props.show) return;
        setGenres([...(props.selectedGenres || [])]);
        setArtists((props.selectedArtists || []).map(toArtist));
        setError('');
        setSaving(false);
    }, [props.show]);

    const saveInfluences = async () => {
        if (!props.profileId) {
            setError('Could not save your influences. Please try again.');
            return;
        }
        setSaving(true);
        setError('');
        const favArtists = artists.map(toFavArtist).map((artist) => ({
            id: artist.id || undefined,
            name: artist.name,
            displayText: artist.displayText,
            url: artist.url,
        }));
        try {
            await axiosService.put(`/profile/${props.profileId}`, {
                favGenres: genres,
                favArtists,
            });
            props.onSaved({ favArtists, favGenres: genres });
            props.setShow(false);
        } catch {
            setError('Could not save your genres and artists. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <JWModal
            title={(
                <div className="pr-8 text-center">
                    <div className="text-xl sm:text-[28px] leading-tight font-extrabold text-gray-900">What&apos;s your sound?</div>
                    <p className="text-sm sm:text-base text-gray-500 font-normal mt-1">
                        Pick a few genres, then tap the artists you love. We use both to match you with people who get it.
                    </p>
                </div>
            )}
            show={props.show}
            onClose={props.onClose}
            setShow={props.setShow}
            wrapperClassName="flex flex-col md:w-[720px] w-full h-[92vh] max-h-[860px] overflow-hidden"
        >
            <div className="flex min-h-0 flex-1 flex-col">
                <div className="pool-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-4 sm:px-4">
                    <TastePicker
                        genres={genres}
                        artists={artists}
                        onGenresChange={setGenres}
                        onArtistsChange={setArtists}
                        dimArtistsUntilReady
                    />
                </div>
                <div className="shrink-0 border-t border-gray-100 px-2 pt-3 sm:px-4">
                    {error && <p className="text-sm text-red-500 text-center mb-2" role="alert">{error}</p>}
                    <JWButton className={PRIMARY_BUTTON_CLASS} onClick={saveInfluences} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </JWButton>
                </div>
            </div>
        </JWModal>
    );
}

export default AddInfluencesModal;
